import { useSnackbar } from 'notistack';
import { useState } from 'react';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { zoomMessages } from 'src/sections/dashboard/zoom/zoomLabels';
import { Event } from '../../../@types/event';

export const useEvents = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<Event | undefined>();
  // R4-E1: the program whose editor is being opened (its pencil spins), and a
  // list refresh in flight (every pencil waits for it).
  const [loadingId, setLoadingId] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  // A failed list read, shown above the table instead of a silently stale list.
  const [listError, setListError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const getList = async () => {
    setRefreshing(true);
    try {
      const response = await axiosInstance.get(APIS.EVENTS.LIST);
      setEvents(response.data.data);
      setListError('');
    } catch (e: any) {
      setListError(
        `${zoomMessages(e, 'Could not load the programs')[0]} The list below may be out of date — reload the page.`
      );
    } finally {
      setRefreshing(false);
    }
  };

  // The editor never opens on the list row (a snapshot that may be minutes
  // old): it reads the program fresh by id, so a save starts from the stored
  // version and carries that version as `expectedUpdatedAt`.
  const openEditor = async (id: string) => {
    setLoadingId(id);
    try {
      const response = await axiosInstance.get(APIS.EVENTS.BY_ID(id));
      setCurrentEvent((response.data?.data ?? response.data) as Event);
      setModalOpen(true);
    } catch (e: any) {
      enqueueSnackbar(zoomMessages(e, 'Could not open this program')[0], { variant: 'error' });
    } finally {
      setLoadingId('');
    }
  };

  /** Kept for callers that open the editor on a row they already hold. */
  const handleEditRow = (selected: Event) => openEditor(selected._id);

  /** Puts a saved program into the list at once (before the refresh lands). */
  const replaceRow = (saved: Event) => {
    setEvents((rows) => {
      const at = rows.findIndex((r) => r._id === saved._id);
      if (at < 0) return rows;
      const next = rows.slice();
      next[at] = saved;
      return next;
    });
  };

  const handleDeleteRow = async (id: string) => {
    try {
      await axiosInstance.delete(APIS.EVENTS.DELETE(id));
      enqueueSnackbar('Deleted Successfully', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error?.message || 'Unable to delete program', { variant: 'error' });
    }
  };

  return {
    events,
    modalOpen,
    setModalOpen,
    currentEvent,
    setCurrentEvent,
    getList,
    openEditor,
    handleEditRow,
    replaceRow,
    loadingId,
    refreshing,
    listError,
    handleDeleteRow,
  };
};
